<template>
  <v-responsive fluid v-resize="resize">
    <v-card min-width="375" rounded="0" :style="$vuetify.theme.dark ? 'background-color: var(--bg-dark);' : 'background-color: var(--bg-light);'">
      <v-card-title primary-title>
        <v-row justify="space-between">
          <v-col cols="10">
            <div v-if="$vuetify.breakpoint.xs" class="primary--text text-overline ml-2">
              Cuentas de {{ tenantName }}
            </div>
            <div v-else class="font-weight-light primary--text text-h4 ml-2 text-capitalize">
              Cuentas de {{ tenantName }}
            </div>
          </v-col>
          <v-col cols="2" class="d-flex justify-end align-center">
            <Dial v-if="hasSuperAdminPermission || hasAdminPermission" />
          </v-col>
        </v-row>
      </v-card-title>

      <v-data-table
        :style="$vuetify.theme.dark ? 'background-color: var(--bg-dark);' : 'background-color: var(--bg-light);'"
        :height="tableHeight"
        :headers="visibleHeaders"
        :items="tableData"
        :loading="tableLoading"
        :page.sync="page"
        :footer-props="footerProps"
        :server-items-length="totalEntries"
        :options.sync="options"
        :search="search"
        class="px-2"
        hide-default-footer
      >
        <template v-slot:footer="{ props }">
          <v-data-footer
            style="height: 60px; justify-content: flex-end"
            :items-per-page-options="props.itemsPerPageOptions"
            :options.sync="options"
            :pagination.sync="props.pagination"
            show-first-last-page
          />
        </template>

        <template v-slot:item.nombreCompleto="{ item }">
          {{ item.nombre ? `${item.nombre} ${item.apellidos}` : item.username }}
        </template>

        <template v-slot:item.roles_names="{ item }">
          {{ getRolesString(item) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center align-center">
            <v-icon 
              color="teal"
              class="mx-1"
              v-if="hasSuperAdminPermission && isEnforceMFA"
              :disabled="!item.email"
              v-on:click.stop
              @click="openConfirmDialog(DIALOGS.ENROLLMENT, item)"
            >
              mdi-qrcode-scan
            </v-icon>
            <v-icon
              color="blue"
              class="mx-1"
              v-if="hasSuperAdminPermission"
              v-on:click.stop
              @click="openConfirmDialog(DIALOGS.RESET_BLOCK, item)"
            >
              mdi-lock-reset
            </v-icon>
            <v-icon
              class="mx-1"
              v-on:click.stop
              @click="viewItem(item)"
            >
              mdi-clipboard-account-outline
            </v-icon>
            <v-icon
              color="orange"
              class="mx-1"
              v-on:click.stop
              @click="editItem(item)"
            >
              mdi-pen
            </v-icon>
            <v-icon 
              color="red"
              class="mx-1"
              v-if="hasSuperAdminPermission"
              v-on:click.stop
              @click="openConfirmDialog(DIALOGS.DELETE, item)"
            >
              mdi-delete
            </v-icon>
          </div>
        </template>
      </v-data-table>

      <ModHeadersTable :headers="headers" />
      <FormCuentaBase />
      <DownloadTemplateAndUpload
        message="subir la plantilla con los comerciales"
        :multiple="false"
        title="Subida de Comerciales"
        templateMutationPath="gerencia/download_template_from_json"
        templatePath="commercialTemplate.json"
        submitMutationPath="gerencia/importFiles"
        submitPath="import_comerciales_xlsx"
        @submitted="onXLSXSubmitted"
      />

      <!-- Unified confirmation dialog -->
      <v-dialog v-model="confirmDialog.visible" width="unset">
        <v-card width="700">
          <div class="d-flex justify-space-between px-4 pt-4">
            <h1 class="px-3 font-weight-light text-center">
              {{ confirmDialog.title }}
            </h1>
            <v-icon size="90" :color="confirmDialog.iconColor">
              {{ confirmDialog.icon }}
            </v-icon>
          </div>
          <div class="pt-4">
            <p class="px-6 font-weight-light text-justify">
              <v-icon :color="confirmDialog.messageIconColor">
                {{ confirmDialog.messageIcon }}
              </v-icon>
              {{ confirmDialog.message }}
            </p>
          </div>
          <v-card-actions>
            <v-spacer />
            <v-btn depressed text color="grey" @click="closeConfirmDialog">
              Cancelar
            </v-btn>
            <v-btn depressed :color="confirmDialog.confirmColor" @click="confirmDialog.onConfirm">
              {{ confirmDialog.confirmLabel }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>

    <div id="control-height" />
  </v-responsive>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import Dial from './Dial.vue';
import FormCuentaBase from '@/components/RRHH/AccountsView/FormCuenta/FormCuentaBase.vue';
import ModHeadersTable from '@/components/GerenciaTeletrabajo/ComercialesTable/ModHeadersTable.vue';
import DownloadTemplateAndUpload from '@/components/DownloadTemplateAndUpload/DownloadTemplateAndUpload.vue';

const DIALOGS = Object.freeze({
  DELETE:     'DELETE',
  RESET_BLOCK: 'RESET_BLOCK',
  ENROLLMENT: 'ENROLLMENT',
});

const DIALOG_DEFAULTS = {
  visible:          false,
  title:            '',
  message:          '',
  icon:             'mdi-alert',
  iconColor:        'orange',
  messageIcon:      'mdi-information',
  messageIconColor: 'grey',
  confirmLabel:     'Confirmar',
  confirmColor:     'primary',
  onConfirm:        () => {},
};

export default {
  name: 'AccountsTable',

  components: { Dial, FormCuentaBase, DownloadTemplateAndUpload, ModHeadersTable },

  data: () => ({
    DIALOGS,
    firstLoaded: true,
    confirmDialog: { ...DIALOG_DEFAULTS },
    tableHeight: 300,
    footerProps: { itemsPerPageOptions: [0] },
    options: {},
    headers: [
      { text: 'Nombre',         value: 'nombreCompleto', sortable: true,  show: true },
      { text: 'Email',          value: 'email',          sortable: true,  show: true },
      { text: 'Rol',            value: 'roles_names',    sortable: true,  show: true },
      { text: 'Tipo comercial', value: 'tipoComercial',  sortable: true,  show: true },
      { text: 'Estado',         value: 'estado',         sortable: true,  show: true },
      { text: 'Last Login',     value: 'lastLogin',      sortable: true,  show: true },
      { text: '',               value: 'actions',        sortable: false, align: 'end', width: '160', show: true },
    ],
  }),

  computed: {
    visibleHeaders() {
      return this.headers.filter(({ show }) => show);
    },
    tenantName() {
      return JSON.parse(localStorage.getItem('tenantInfo'))?.name ?? '';
    },
    page: {
      get()    { return this.$store.state.rrhh.cuentasTable.page; },
      set(val) { this.$store.commit('rrhh/cuentasTablePage', val); },
    },
    ...mapState('rrhh', {
      tableData:    (state) => state.cuentasTable.cuentas,
      totalEntries: (state) => state.cuentasTable.totalEntries,
      tableLoading: (state) => state.cuentasTable.isLoading,
    }),
    ...mapState('util', {
      search: (state) => state.navBarSearchField,
    }),
    ...mapGetters('login', ['hasAdminPermission', 'hasSuperAdminPermission', 'isEnforceMFA']),
  },

  watch: {
    options: {
      deep: true,
      handler() {
        const snapshot = this.options.page;
        setTimeout(() => {
          if (this.options.page === snapshot && !this.firstLoaded && !this.tableLoading) {
            this.updateCuentasTableParams(this.options);
            this.getCuentasTableData();
          }
        }, 200);
      },
    },
    search() {
      const text = this.search;
      setTimeout(() => {
        if (text !== this.search) return;
        this.options.page = 1;
        this.updateCuentasTableParams({ search: this.search });
        this.getCuentasTableData();
      }, 300);
    },
    tableHeight(val) {
      const perPage = Math.floor(val / 48 - 1);
      this.footerProps.itemsPerPageOptions = [perPage];
      this.options.itemsPerPage = perPage;
      this.firstLoaded = false;
    },
  },

  methods: {
    // ── Table actions ────────────────────────────────────────────────────────
    editItem(item) {
      this.openEditarCuentaDialogo(item);
    },
    viewItem(item) {
      this.openCuentaDialogo(item);
    },
    onXLSXSubmitted() {
      this.getCuentasTableData();
    },
    getRolesString(comercial) {
      return this.$store.state.general.rolesDecode[comercial.rol.id];
    },
    resize() {
      this.tableHeight =
        document.getElementById('control-height').clientHeight - 40 - 70 - 32 - 8 - 16 - 15;
    },

    // ── Confirmation dialog ──────────────────────────────────────────────────
    openConfirmDialog(type, item) {
      const configs = {
        [DIALOGS.DELETE]: {
          title:            `¿Borrar a ${item.username}?`,
          message:          'Recuerde proceder con cautela. Este cambio podría ser irreversible y afectar a la consistencia de los datos existentes en la base de datos. Consulte con los administradores si no está seguro.',
          icon:             'mdi-alert',
          iconColor:        'orange',
          messageIcon:      'mdi-delete',
          messageIconColor: 'red',
          confirmLabel:     'Borrar',
          confirmColor:     'error',
          onConfirm:        () => this.handleDeleteItem(item),
        },
        [DIALOGS.RESET_BLOCK]: {
          title:            `¿Resetear el bloqueo de ${item.username}?`,
          message:          'Se desbloqueará el acceso del usuario en AXES. Esta acción es segura pero asegúrese de que el usuario lo necesita.',
          icon:             'mdi-lock-reset',
          iconColor:        'blue',
          messageIcon:      'mdi-information',
          messageIconColor: 'blue',
          confirmLabel:     'Resetear',
          confirmColor:     'primary',
          onConfirm:        () => this.handleResetAxesBlock(item),
        },
        [DIALOGS.ENROLLMENT]: {
          title:            `¿Enviar invitación OTP a ${item.email}?`,
          message:          'Se enviará un correo con el QR de enrolamiento al usuario. Asegúrese de que la dirección de email es correcta antes de continuar.',
          icon:             'mdi-qrcode-scan',
          iconColor:        'teal',
          messageIcon:      'mdi-email-send',
          messageIconColor: 'teal',
          confirmLabel:     'Enviar',
          confirmColor:     'success',
          onConfirm:        () => this.handleSendEnrollment(item),
        },
      };

      this.confirmDialog = { ...DIALOG_DEFAULTS, ...configs[type], visible: true };
    },
    closeConfirmDialog() {
      this.confirmDialog = { ...DIALOG_DEFAULTS };
    },

    // ── Confirmed action handlers ────────────────────────────────────────────
    handleDeleteItem(item) {
      this.borrarCuenta(item).then(() => this.closeConfirmDialog());
    },
    handleResetAxesBlock(item) {
      this.resetAxesLockout({ username: item.username });
      this.closeConfirmDialog();
    },
    handleSendEnrollment(item) {
      this.sendEnrollmentInvite({ email: item.email });
      this.closeConfirmDialog();
    },

    // ── Vuex ─────────────────────────────────────────────────────────────────
    ...mapActions('rrhh', [
      'getCuentasTableData',
      'updateCuentasTableParams',
      'openEditarCuentaDialogo',
      'openCuentaDialogo',
      'borrarCuenta',
    ]),
    ...mapActions('login',   ['resetAxesLockout']),
    ...mapActions('otpauth', ['sendEnrollmentInvite']),
  },
};
</script>

<style scoped>
#control-height {
  height: 100vh;
  width: 0;
  position: absolute;
}
</style>